import Flex from '@Components/Bases/Flex';
import { useGetProgress } from '@Domains/Progress';
import { IReport } from '@Domains/Reports';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import Download from '@Router/Landing/Containers/ReportDetail/DownloadCVUP/DownloadCVUP';
import { formatDate } from '@Utils/datetime';
import { useLocation } from 'react-router-dom';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { ReportType } from 'src/Enum/Report';
import PdfCvupIcon from '../../../../Components/Icons/PdfCvupIcon';
import DeleteReportButton from '../DeleteReportButton';
import CGRScoreSection from './CGRScoreSection/CGRScoreSection';
import BusinessOperationProgress from './ReportProgress/BusinessOperationProgress';
import ESGDataPlatform from './ReportProgress/ESGProgress';
import GoalAndOperationalPlansProgress from './ReportProgress/GoalAndOperationalPlansProgress';
import ReportPublishing from './ReportProgress/ReportPublishing';
import SettingYear from './ReportProgress/SettingReportYear';
import StepFromBoardDirector from './ReportProgress/StepFromBoardDirector';

type ReportDetailProps = {
  reportDetail?: IReport;
  refetch: () => void;
  allReports?: IReport[];
};

const ReportDetail = ({
  reportDetail,
  refetch,
  allReports = [],
}: ReportDetailProps) => {
  const { user } = useProfile();
  const companyId = user?.I_COMPANY_ID?.toString() || '';

  const [{ data, loading, error }, refetchProgress] = useGetProgress(
    Number(reportDetail?.id),
    companyId
  );

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryReport = queryParams.get('report');

  const isExpanded = reportDetail?.id === Number(queryReport);

  const defaultExpanded =
    allReports.length > 0 && reportDetail?.id === allReports[0]?.id;

  const handleRefetch = async () => {
    await refetchProgress();
    refetch();
  };

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  if (error) return <Typography>Error loading progress data</Typography>;

  if (!data) return <Typography>No progress data available</Typography>;

  const {
    cgrScore,
    businessOperation,
    goalsAndOperationalPlans,
    esgPlans,
    summarizeReport,
    report,
  } = data;

  const isPublished = Boolean(summarizeReport.publishedDate);

  return (
    <Box width={1} mb={1}>
      <Accordion
        key={`${reportDetail?.startOperatingYear}-${reportDetail?.id}`}
        sx={{ mb: 1 }}
        defaultExpanded={queryReport ? isExpanded : defaultExpanded}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          aria-controls={`report-detail-${reportDetail?.startOperatingYear}`}
          id={`report-detail-${reportDetail?.startOperatingYear}`}
        >
          <Flex width={1} justifyContent="space-between" alignItems="center">
            <Flex gap={1} alignItems="center">
              <Box height={40} width={6} bgcolor="primary.main" />
              <PdfCvupIcon />
              <Typography
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {`แบบ CVUP ${
                  reportDetail?.reportType === ReportType.SINGLE
                    ? reportDetail?.startOperatingYear
                    : `${reportDetail?.startOperatingYear} - ${reportDetail?.endOperatingYear}`
                }`}
              </Typography>
            </Flex>
            {isPublished && summarizeReport?.publishedDate && (
              <Typography bgcolor="primary.lighter" p={0.5}>
                เผยแพร่เมื่อ {''}
                {formatDate(summarizeReport?.publishedDate, 'th-TH')}
              </Typography>
            )}
          </Flex>
        </AccordionSummary>
        <AccordionDetails>
          <Flex width={1} gap={2}>
            <Box width={1} flex={1}>
              <SettingYear
                reportDetail={reportDetail}
                refetchProgress={handleRefetch}
              />
              <CGRScoreSection cgr={cgrScore} />
              <BusinessOperationProgress
                businessOperation={businessOperation}
                reportId={reportDetail?.id}
                isPublished={isPublished}
              />
              <GoalAndOperationalPlansProgress
                goalsAndOperationalPlans={goalsAndOperationalPlans}
                reportId={reportDetail?.id}
                isPublished={isPublished}
              />
              <ESGDataPlatform
                esg={esgPlans}
                reportId={reportDetail?.id}
                isPublished={isPublished}
              />
              <StepFromBoardDirector
                score={cgrScore?.score ?? 0}
                summarizeReport={summarizeReport}
                reportId={reportDetail?.id}
              />
              <ReportPublishing
                score={cgrScore?.score ?? 0}
                summarizeReport={summarizeReport}
                reportId={reportDetail?.id}
              />
              <DeleteReportButton
                id={reportDetail?.id}
                startOperatingYear={reportDetail?.startOperatingYear}
                endOperatingYear={reportDetail?.endOperatingYear}
                refetch={refetch}
                isPublished={isPublished}
              />
            </Box>
            <Download report={report} />
          </Flex>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ReportDetail;
